import {
  SearchOperator,
  SubmittableSearchFormField,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type AdvancedSearchQuery = {
  fields: SubmittableSearchFormField[];
  searchMode: SearchOperator;
  version?: string;
};

export type BriefAdvancedSearchData = {
  id: string;
  owner: { id: string };
  name: string;
  shared: boolean;
  query: AdvancedSearchQuery;
  createdAt: Date;
};

export type CreateAdvancedSearch = Pick<
  BriefAdvancedSearchData,
  "name" | "query" | "shared"
>;

const addAdvancedSearch = async ({
  resource,
  create,
}: {
  resource: string;
  create: CreateAdvancedSearch;
}) => {
  return await api.post<BriefAdvancedSearchData>(
    `back/api/advanced-search/${resource}`,
    create,
  );
};

export const useAddAdvancedSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addAdvancedSearch,
    onSuccess: (_, v) =>
      queryClient.invalidateQueries({
        queryKey: ["advanced-search", v.resource],
      }),
  });
};

const deleteAdvancedSearch = async (id: string) => {
  return await api.delete<BriefAdvancedSearchData>(
    `back/api/advanced-search/${id}`,
  );
};

export const useDeleteAdvancedSearch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAdvancedSearch,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["advanced-search"] }),
  });
};

const getAdvancedSearchByResource = async (resource: string) => {
  const { data } = await api.get<BriefAdvancedSearchData[]>(
    `back/api/advanced-search/${resource}`,
  );
  return data;
};

export const useGetAdvancedSearchByResource = (resource: string) => {
  return useQuery({
    queryFn: () => getAdvancedSearchByResource(resource),
    queryKey: ["advanced-search", resource],
  });
};
